import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {RelatedPartyViewModel} from './view-models/related-party-view.model';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {TableTemplateComponentHost} from '@components/ui/table-template/table-template-component-host';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {RelatedPartyConverterService} from './view-models/related-party-converter.service';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {RelatedPartyService} from './services/related-party.service';
import {AppParameterMap} from '../../config/app-parameter-map';
import {PARAM_ENTITY_ID} from '../../config/app-urls';
import {Router} from '@angular/router';
import {Keys} from '@shared/keys';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {TableTemplateComponent} from '@components/ui/table-template/table-template.component';
import {RelatedPartyCardComponent} from './related-party-card/related-party-card.component';

@Component({
    selector: 'app-related-parties',
    templateUrl: './related-parties.component.html',
    styleUrls: ['./related-parties.component.scss'],
    providers: [ClientsideDataService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingAnimationComponent, TableTemplateComponent, RelatedPartyCardComponent]
})
export class RelatedPartiesComponent implements OnInit, TableTemplateComponentHost {

  private readonly router = inject(Router);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly relatedPartyService = inject(RelatedPartyService);
  private readonly converter = inject(RelatedPartyConverterService);
  private readonly clientsideDataService = inject(ClientsideDataService<RelatedPartyViewModel>);

  private readonly SEARCH_FIELDS: Keys<RelatedPartyViewModel> = ['name', 'description'];

  readonly dataLoading = signal(true);
  readonly dataAvailable = signal(false);
  readonly dataEmpty = signal(false);
  readonly columns = signal<TableTemplateColumnConfig[]>([]);
  readonly sortField = signal('description');
  readonly sortOrder = signal(1);
  readonly searchString = signal('');
  readonly pageNumber = signal(1);
  readonly pageSize = signal(20);
  readonly currentPage = signal<PageViewModel<RelatedPartyViewModel>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 20,
    data: []
  });

  ngOnInit(): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.populateColumns();
    this.loadSavedSort();

    const params = AppParameterMap.from(this.router);
    const entityId = params.get(PARAM_ENTITY_ID);

    this.relatedPartyService.getRelatedParties(entityId).subscribe(relatedPartyListDto => {
      const relatedParties = this.converter.convertRelatedPartyListDtoToRelatedPartyViewModels(relatedPartyListDto);
      this.clientsideDataService.initialize(relatedParties);
      this.updateData();
      this.dataLoading.set(false);
      this.dataEmpty.set(relatedParties.length === 0);
      this.dataAvailable.set(!this.dataEmpty());
    });
  }

  private loadSavedSort() {
    const storedSort = this.sessionStorageService.getSort(SortedTable.RelatedParties);
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }
  }

  private updateData(): void {
    this.currentPage.set(this.clientsideDataService.generatePage(this.sortField(),
      this.sortOrder() === 1,
      this.SEARCH_FIELDS,
      this.searchString(),
      this.pageNumber(),
      this.pageSize()));
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.pageNumber.set(1);
    this.updateData();
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }

    this.sessionStorageService.setSort(SortedTable.RelatedParties, this.sortField(), this.sortOrder());
  }

  search(searchTerm: string): void {
    this.searchString.set(searchTerm);
    this.pageNumber.set(1);
    this.updateData();
  }

  page(targetPage: number): void {
    this.pageNumber.set(targetPage);
    this.updateData();
  }

  rowClicked(_dataViewModel: DataViewModel): void {
    // Not supported for related parties
  }

  private populateColumns() {
    this.columns.set([
      {
        title: '',
        dataProperty: 'icon',
        cellType: CellType.Icon,
        sortable: false,
        alignment: Alignment.Center,
        width: '9%',
        dataFocusable: false,
        headingFocusable: false
      },
      {
        title: 'Name',
        dataProperty: 'name',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '33%',
        dataFocusable: true,
        headingFocusable: true
      },
      {
        title: 'Role',
        dataProperty: 'description',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '22%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Appointment Date',
        dataProperty: 'dateAppointed',
        cellType: CellType.Date,
        sortable: true,
        alignment: Alignment.Left,
        width: '18%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Resignation Date',
        dataProperty: 'dateResigned',
        cellType: CellType.Date,
        sortable: true,
        alignment: Alignment.Left,
        width: '18%',
        dataFocusable: false,
        headingFocusable: true
      }
    ]);
  }
}
